/**
 * Performance Monitor
 * Tracks app launch time, tap latency, and other performance metrics
 */

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private appStartTime: number = Date.now();

  /**
   * Start measuring a metric
   */
  start(name: string): void {
    this.metrics.set(name, {
      name,
      startTime: Date.now(),
    });
  }

  /**
   * End measuring a metric and return duration
   */
  end(name: string): number | null {
    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`Performance metric "${name}" not found`);
      return null;
    }

    const endTime = Date.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;

    this.metrics.set(name, metric);

    // Log if exceeds thresholds
    if (name === 'app_launch' && duration > 2000) {
      console.warn(`⚠️ App launch took ${duration}ms (target: <2000ms)`);
    } else if (name.includes('tap') && duration > 100) {
      console.warn(`⚠️ Tap latency ${duration}ms (target: <100ms)`);
    }

    return duration;
  }

  /**
   * Measure app launch time (call when app is ready)
   */
  markAppReady(): number {
    const duration = Date.now() - this.appStartTime;
    console.log(`✅ App launched in ${duration}ms`);
    return duration;
  }

  /**
   * Measure tap-to-feedback latency
   */
  measureTapLatency(actionName: string, callback: () => void | Promise<void>): void {
    const metricName = `tap_${actionName}`;
    this.start(metricName);

    // Execute callback
    const result = callback();

    // Handle async callbacks
    if (result instanceof Promise) {
      result.then(() => {
        const duration = this.end(metricName);
        if (duration && duration < 100) {
          console.log(`✅ ${actionName} tap latency: ${duration}ms`);
        }
      });
    } else {
      const duration = this.end(metricName);
      if (duration && duration < 100) {
        console.log(`✅ ${actionName} tap latency: ${duration}ms`);
      }
    }
  }

  /**
   * Get all recorded metrics
   */
  getAllMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Get a specific metric
   */
  getMetric(name: string): PerformanceMetric | undefined {
    return this.metrics.get(name);
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
    this.appStartTime = Date.now();
  }

  /**
   * Log summary of all metrics
   */
  logSummary(): void {
    console.log('=== Performance Summary ===');
    for (const metric of this.metrics.values()) {
      if (metric.duration) {
        console.log(`${metric.name}: ${metric.duration}ms`);
      }
    }
    console.log('===========================');
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();
